import { useState, useEffect, useRef, useCallback } from 'react'
import { ApiResponse } from 'src/types/api.types'
import type { DynamicPricingResponse } from '@query/page-query/pricing-query/pricing.model'

type DynamicPricingOptions = {
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number
  /** Initial annual spend value */
  initialSpend?: number
  /** Currency (default: USD) */
  currency?: 'USD' | 'INR'
  /** Billing cycle (default: ANNUAL) */
  billingCycle?: 'MONTH' | 'ANNUAL'
  /** Callback on successful fetch */
  onSuccess?: (data: DynamicPricingResponse) => void
  /** Callback on error */
  onError?: (error: Error) => void
  /** Amount limit */
  amountLimit?: number
}

/**
 * Fetches dynamic pricing from the Next.js API route
 */
async function fetchDynamicPricingFromApi(
  maxAnnualSpend: number,
  currency = 'USD',
  billingCycle = 'ANNUAL'
): Promise<DynamicPricingResponse> {
  const params = new URLSearchParams({
    max_annual_spend: maxAnnualSpend.toString(),
    currency,
    billing_cycle: billingCycle,
  })

  const response = await fetch(`/api/v1/pricing/dynamic-pricing?${params}`)
  const json: ApiResponse<DynamicPricingResponse> = await response.json()

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch dynamic pricing')
  }

  return json.data
}

/**
 * Hook to fetch dynamic pricing with debouncing
 *
 * @example
 * ```tsx
 * const {
 *   pricing,
 *   isLoading,
 *   setAnnualSpend,
 *   annualSpend
 * } = useDynamicPricing({
 *   initialSpend: 20000,
 *   debounceMs: 500,
 * })
 *
 * // In your input handler
 * const handleSpendChange = (value: number) => {
 *   setAnnualSpend(value)
 * }
 *
 * // Display
 * if (isLoading) return <Skeleton />
 * return <span>${pricing?.price_usd}</span>
 * ```
 */
export const useDynamicPricing = ({
  debounceMs = 500,
  initialSpend = 200000,
  currency = 'USD',
  billingCycle = 'ANNUAL',
  onSuccess,
  onError,
  amountLimit,
}: DynamicPricingOptions = {}) => {
  const [annualSpend, setAnnualSpend] = useState<string>(
    initialSpend.toString()
  )
  const [pricing, setPricing] = useState<DynamicPricingResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Ref to track the latest request (for debounce cancellation)
  const abortControllerRef = useRef<AbortController | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Fetch function
  const fetchPricing = useCallback(
    async (spend: number) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Don't fetch if spend is 0 or invalid
      if (!spend || spend <= 0) {
        const data = await fetchDynamicPricingFromApi(1, 'USD', 'ANNUAL')
        setPricing(data)
        onSuccess?.(data)
        return
      }
      if (spend && amountLimit && spend > amountLimit) {
        setPricing({
          plan_name: 'Enterprise',
          charge_rate: 0,
          price_usd: 0,
          label_price_usd: 0,
          discount: 0,
          trial_period: 0,
          projected_net_savings: 0,
          plan_type: 'ENTERPRISE',
          max_annual_spend: 0,
          plan_id: '',
          cloud_spend_range_id: '',
          monthly_price_for_annual_plan: 0,
          comparable_plans: {},
          projected_net_saving_rate: 0,
          credits: 0,
          price: 0,
          currency: 'USD',
          label_price: 0,
        })
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await fetchDynamicPricingFromApi(
          spend,
          currency,
          billingCycle
        )
        setPricing(data)
        onSuccess?.(data)
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err)
          onError?.(err)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [currency, billingCycle, onSuccess, onError]
  )

  const convertedAnnualSpend = Number(annualSpend)
  // Debounced fetch effect
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      void fetchPricing(convertedAnnualSpend)
    }, debounceMs)

    // Cleanup on unmount or value change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [annualSpend, debounceMs, fetchPricing])

  // Initial fetch on mount
  useEffect(() => {
    if (initialSpend > 0) {
      void fetchPricing(initialSpend)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    /** Current pricing data from API */
    pricing,
    /** Whether a fetch is in progress */
    isLoading,
    /** Any error that occurred */
    error,
    /** Current annual spend value */
    annualSpend,
    /** Function to update the annual spend (triggers debounced fetch) */
    setAnnualSpend,
    /** Force refetch with current spend */
    refetch: () => fetchPricing(convertedAnnualSpend),
  }
}
